import type { Question } from '../../types';

// ---------------------------------------------------------------------------
// General Contractor Supplement
// Source: Gateway Underwriters A60 (07/13) — General Contractors GL Supp
// ---------------------------------------------------------------------------

const onlyIfGC = [
  { field: 'industry.subcategory', operator: 'eq' as const, value: 'general_contractor' },
];

const onlyIfGCUsesSubs = [
  ...onlyIfGC,
  { field: 'gc.uses_subs', operator: 'eq' as const, value: true },
];

export const generalContractorQuestions: Question[] = [
  // -- Identity / Operations -------------------------------------------------
  {
    id: 'gc_years_experience',
    text: 'How many years of experience do you have in general contracting?',
    section: 'supplement',
    sort_order: 100,
    field_type: 'number',
    suffix: 'years',
    required: true,
    field_path: 'gc.years_experience',
    conditions: onlyIfGC,
    tags: ['gc_supp', 'carrier_required'],
  },
  {
    id: 'gc_role_type',
    text: 'How do you primarily operate?',
    subtext: 'Select all that apply.',
    section: 'supplement',
    sort_order: 110,
    field_type: 'multiselect',
    options: [
      { label: 'General Contractor', value: 'gc' },
      { label: 'Project Manager', value: 'pm' },
      { label: 'Project Owner', value: 'owner' },
      { label: 'Builder / Developer', value: 'developer' },
      { label: 'Construction Manager', value: 'cm' },
    ],
    required: true,
    field_path: 'gc.role_types',
    conditions: onlyIfGC,
    tags: ['gc_supp', 'carrier_required'],
  },
  {
    id: 'gc_eo_coverage',
    text: 'Do you carry separate E&O coverage for your project/construction management work?',
    section: 'supplement',
    sort_order: 120,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.has_eo_coverage',
    conditions: [...onlyIfGC, { field: 'gc.role_types', operator: 'contains', value: 'pm' }],
  },
  {
    id: 'gc_pct_gc',
    text: 'What percentage of your work is as a General Contractor?',
    section: 'supplement',
    sort_order: 130,
    field_type: 'percentage',
    required: true,
    field_path: 'gc.pct_gc',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_pct_sub',
    text: 'What percentage of your work is as a Subcontractor (working for another GC)?',
    section: 'supplement',
    sort_order: 140,
    field_type: 'percentage',
    required: true,
    field_path: 'gc.pct_sub',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_pct_developer',
    text: 'What percentage of your work is as a Developer?',
    section: 'supplement',
    sort_order: 150,
    field_type: 'percentage',
    required: true,
    field_path: 'gc.pct_developer',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_pct_cm',
    text: 'What percentage of your work is as a Construction Manager?',
    section: 'supplement',
    sort_order: 160,
    field_type: 'percentage',
    required: true,
    field_path: 'gc.pct_cm',
    conditions: onlyIfGC,
  },

  // -- License / Authority ---------------------------------------------------
  {
    id: 'gc_licensed',
    text: 'Are you licensed?',
    section: 'supplement',
    sort_order: 200,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.is_licensed',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_license_class',
    text: 'License class and number',
    section: 'supplement',
    sort_order: 210,
    field_type: 'text',
    placeholder: 'e.g., Class A #12345',
    required: true,
    field_path: 'gc.license_class',
    conditions: [...onlyIfGC, { field: 'gc.is_licensed', operator: 'eq', value: true }],
  },
  {
    id: 'gc_operating_states',
    text: 'In which states do you operate?',
    section: 'supplement',
    sort_order: 220,
    field_type: 'state_select',
    required: true,
    field_path: 'gc.operating_states',
    conditions: onlyIfGC,
    tags: ['carrier_required'],
  },
  {
    id: 'gc_license_shared',
    text: 'Do you allow your license to be used by others to obtain a permit without your jobsite supervision?',
    section: 'supplement',
    sort_order: 230,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.license_shared',
    conditions: onlyIfGC,
  },

  // -- Owners / Employees ----------------------------------------------------
  {
    id: 'gc_num_owners',
    text: 'How many active owners do you have?',
    section: 'supplement',
    sort_order: 300,
    field_type: 'number',
    required: true,
    field_path: 'gc.num_owners',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_total_owner_payroll',
    text: 'What is the total owner payroll?',
    subtext: 'Carriers will apply state minimum payroll requirements where applicable.',
    section: 'supplement',
    sort_order: 310,
    field_type: 'currency',
    required: true,
    field_path: 'gc.total_owner_payroll',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_employee_trades',
    text: 'List your employee trades and payroll',
    subtext:
      'Include all classifications and class codes (e.g., 5403 Carpentry, 5474 Painting). For each, provide annual payroll.',
    section: 'supplement',
    sort_order: 320,
    field_type: 'trade_matrix',
    required: true,
    field_path: 'gc.employee_trades',
    conditions: onlyIfGC,
    tags: ['carrier_required'],
  },

  // -- Subcontractor Management ---------------------------------------------
  {
    id: 'gc_uses_subs',
    text: 'Do you use subcontractors?',
    section: 'supplement',
    sort_order: 400,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.uses_subs',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_sub_wc_required',
    text: 'Do you require Workers Compensation certificates from all subcontractors?',
    section: 'supplement',
    sort_order: 410,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.requires_sub_wc',
    conditions: onlyIfGCUsesSubs,
  },
  {
    id: 'gc_sub_gl_required',
    text: 'Do all subcontractors provide Certificates of General Liability Insurance?',
    section: 'supplement',
    sort_order: 420,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.requires_sub_gl',
    conditions: onlyIfGCUsesSubs,
  },
  {
    id: 'gc_sub_gl_limits',
    text: 'What GL limits do you require from your subcontractors?',
    section: 'supplement',
    sort_order: 430,
    field_type: 'currency',
    required: true,
    field_path: 'gc.sub_gl_limits_required',
    conditions: onlyIfGCUsesSubs,
  },
  {
    id: 'gc_additional_insured',
    text: 'Are you named as an additional insured on all subcontractor certificates?',
    section: 'supplement',
    sort_order: 440,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.additional_insured',
    conditions: onlyIfGCUsesSubs,
  },
  {
    id: 'gc_hold_harmless',
    text: 'Is a favorable hold-harmless agreement part of your contracts with subcontractors?',
    section: 'supplement',
    sort_order: 450,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.hold_harmless',
    conditions: onlyIfGCUsesSubs,
  },
  {
    id: 'gc_cert_retention',
    text: 'How long are subcontractor certificates kept on file?',
    section: 'supplement',
    sort_order: 460,
    field_type: 'text',
    placeholder: 'e.g., 5 years',
    required: true,
    field_path: 'gc.cert_retention',
    conditions: onlyIfGCUsesSubs,
  },

  // -- Work Matrix -----------------------------------------------------------
  {
    id: 'gc_work_matrix',
    text: 'Indicate who performs each type of work',
    subtext:
      'For each trade, mark whether the work is done by your employees, insured subcontractors, or uninsured subcontractors.',
    section: 'supplement',
    sort_order: 500,
    field_type: 'trade_matrix',
    required: true,
    field_path: 'gc.work_matrix',
    conditions: onlyIfGC,
    tags: ['carrier_required'],
    // Matrix rows: Carpentry-Interior, Carpentry-All Other, Concrete, Demolition,
    // Door/Window, Drywall, Electrical, Excavation, Floor Covering, Home Furnishings,
    // Insulation, Masonry, Painting-Exterior, Painting-Interior, Plaster, Plumbing,
    // Re-Roofing, Siding, Tiling, Other
  },

  // -- Construction Mix -----------------------------------------------------
  {
    id: 'gc_residential_mix',
    text: 'Residential work breakdown',
    subtext: 'What percentage is new construction, remodeling, or demolition? (must total 100%)',
    section: 'supplement',
    sort_order: 600,
    field_type: 'trade_matrix',
    required: false,
    field_path: 'gc.residential_mix',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_commercial_mix',
    text: 'Commercial work breakdown',
    subtext: 'What percentage is new construction, remodeling, or demolition? (must total 100%)',
    section: 'supplement',
    sort_order: 610,
    field_type: 'trade_matrix',
    required: false,
    field_path: 'gc.commercial_mix',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_largest_project',
    text: 'What is the value of your largest single project in the past year?',
    section: 'supplement',
    sort_order: 700,
    field_type: 'currency',
    required: true,
    field_path: 'gc.largest_project_value',
    conditions: onlyIfGC,
    tags: ['carrier_required'],
  },
  {
    id: 'gc_works_condos',
    text: 'Do you work on condominiums, townhouses, or tract homes?',
    section: 'supplement',
    sort_order: 710,
    field_type: 'boolean',
    required: true,
    field_path: 'gc.works_condos_tract',
    conditions: onlyIfGC,
  },
  {
    id: 'gc_max_height',
    text: 'What is the maximum height (in stories) of buildings you work on?',
    section: 'supplement',
    sort_order: 720,
    field_type: 'number',
    suffix: 'stories',
    required: true,
    field_path: 'gc.max_height',
    conditions: onlyIfGC,
  },
];
